/* The wire, in TypeScript.
 *
 * Mirrors api/hgr/schemas.py field for field. That file says it plainly: "a
 * field renamed in this file is a broken page", and this is the other half of
 * that sentence. Nothing here is invented, nothing here is optional-because-it-
 * was-convenient — a field is `| null` here only where pydantic says it can be
 * null, so a page that forgets to handle the empty state fails at compile time
 * instead of at 20:00 on a Sunday.
 *
 * Two conventions carried over from the backend:
 *   - Every datetime is an ISO-8601 string in UTC with a trailing `Z`. They are
 *     typed as `string`, never `Date`: JSON has no date type, and a field typed
 *     `Date` that actually holds a string is the bug that survives a refactor.
 *     Everything goes through format.ts, which knows about time zones.
 *   - Colours are TOKENS, never hex. tokens.css owns the palette, because every
 *     accent has one fixed text rule and a hex value does not carry that rule
 *     with it.
 */

/* ------------------------------------------------------------------ palette */

/** Every colour the API may return. hgr/colors.py holds the same list. */
export type ColorToken = 'sky' | 'cyan' | 'magenta' | 'blue' | 'deep' | 'ink' | 'paper';

/**
 * The five a genre chip may use. The two dark fields are excluded on the server
 * (hgr/colors.py): a dark chip inline next to body text reads as a selected
 * state rather than as a genre.
 */
export type GenreColorToken = Extract<ColorToken, 'sky' | 'cyan' | 'magenta' | 'blue' | 'paper'>;

/** In the order tokens.css lists them. Useful for a swatch row. */
export const COLOR_TOKENS: readonly ColorToken[] = [
  'sky',
  'cyan',
  'magenta',
  'blue',
  'deep',
  'ink',
  'paper',
];

/* -------------------------------------------------------------- enumerations
 * StrEnums on the server, so they arrive as their lowercase values. */

export type SlotKind = 'live' | 'prerecorded' | 'repeat' | 'fallback';
export type SlotStatus = 'scheduled' | 'on_air' | 'aired' | 'cancelled';
export type HostStatus = 'active' | 'archived';
export type EpisodeStatus = 'draft' | 'published' | 'takedown';

/**
 * WHICH LICENCE lets an episode's recording sit where it sits — the two-speed
 * archive. `EpisodeStatus` answers "may the public see this page"; this answers
 * "may we hold this audio, and for how long". Different questions, and an
 * episode can be `published` under any tier. hgr/models.py holds the same
 * list and docs/DATA_MODEL.md holds the reasoning.
 *
 *   sena_replay     Tier C, the default and the safe one. An ordinary DJ set of
 *                   other people's records. No on-demand licence for that exists
 *                   at any published Dutch tariff (D2), so our own copy lives
 *                   inside Sena's replay allowance and then comes off the web.
 *   own_repertoire  Tier A. Flytree Records releases, station commissions, talk and
 *                   interview content — the station holds the producer right
 *                   itself under art. 6 WNR, the one right no Dutch CMO can sell
 *                   us and therefore the only basis for a permanent copy here.
 *   direct_licence  Tier A. Same permanence, different evidence: a named grantor
 *                   gave written permission for these specific recordings.
 *   soundcloud_only The official SoundCloud widget is the only public audio.
 *                   It does not permit a local copy or station playout.
 *   mixcloud_only   Tier B. We hold no audio at all. Mixcloud has the only copy
 *                   and the page links out to it.
 *
 * The client cares because these are four different pages, not a cosmetic
 * label — see pages/Episode.tsx. Above all: Tier A audio is outside the Sena
 * online radio licence, so it keeps a scrubber; Tier C audio is inside it, and
 * Sena art. 4(e) allows only a fixed forward jump of at least five minutes.
 */
export type Clearance =
  | 'sena_replay'
  | 'own_repertoire'
  | 'direct_licence'
  | 'soundcloud_only'
  | 'mixcloud_only';

/**
 * The Tier A test, in one place so no page re-derives it. True means the
 * recording side is genuinely cleared and the self-hosted copy is permanent;
 * false means the audio is either on Sena's two-week clock or not ours at all.
 *
 * Written as an explicit disjunction rather than `!== 'sena_replay'` on
 * purpose: a fifth clearance added on the server must fail closed here, as
 * temporary, rather than silently inherit permanence.
 */
export function isPermanentClearance(clearance: Clearance): boolean {
  return clearance === 'own_repertoire' || clearance === 'direct_licence';
}

export type SubmissionState =
  | 'submitted'
  | 'in_review'
  | 'accepted'
  | 'rejected'
  | 'changes_requested'
  | 'scheduled'
  | 'published'
  | 'archived'
  | 'takedown';

export type SubmissionKind = 'oneoff' | 'residency' | 'outsiders';
export type SubmissionFormat = 'live' | 'prerecorded';
export type StaffRole = 'admin' | 'programming';
export type EpisodeSort = 'newest' | 'oldest' | 'staff';

/* -------------------------------------------------------------------- refs */

export interface GenreRef {
  slug: string;
  name: string;
  color_token: ColorToken;
}

export interface HostRef {
  slug: string;
  name: string;
}

export interface ShowRef {
  slug: string;
  title: string;
}

export interface StaffRef {
  id: string;
  name: string;
  role: StaffRole;
}

/* ------------------------------------------------------------ GET /api/live */

export interface StreamInfo {
  url: string;
  bitrate: number;
  format: string;
}

/**
 * What is on air, assembled from the schedule rather than from ICY metadata
 * (D8). `dj_connected` is false while a slot is open but nobody is on the
 * harbor — the player says "starting shortly" instead of showing a dead stream
 * as live.
 */
export interface NowPlaying {
  kind: SlotKind;
  catalogue_no: string;
  title: string;
  show: ShowRef | null;
  hosts: HostRef[];
  starts_at: string;
  ends_at: string;
  genres: GenreRef[];
  artwork_color: ColorToken;
  location_short: string;
  elapsed_seconds: number;
  dj_connected: boolean;
}

export interface UpNext {
  catalogue_no: string;
  title: string;
  starts_at: string;
  ends_at: string;
  kind: SlotKind;
  hosts: HostRef[];
  artwork_color: ColorToken;
  /** Filled in by the live router; the client counts down from it. */
  seconds_until: number | null;
}

export interface FallbackPlaying {
  catalogue_no: string;
  title: string;
  hosts: HostRef[];
}

/**
 * D7. `/live` is fallible and fed only during shows; `/offair` is always up on
 * the identical codec. This is what the stream is actually serving, so the
 * player never shows a dead play button.
 */
export interface FallbackState {
  kind: 'archive_shuffle' | 'silence' | 'unknown';
  label: string;
  playing: FallbackPlaying | null;
}

/** Polled every 15s. The most important response on the station. */
export interface LiveState {
  live: boolean;
  server_time: string;
  stream: StreamInfo;
  now: NowPlaying | null;
  next: UpNext[];
  fallback: FallbackState | null;
}

/* ------------------------------------------------------------- GET /api/chat
 *
 * One permanent station room is available on every page and between live sets.
 * Keeping its id on every write still prevents a stale client from posting into
 * a future room shape if the contract ever changes.
 */

export interface ChatRoom {
  id: string;
  title: string;
  starts_at: string | null;
  ends_at: string | null;
  writable: boolean;
  closable: boolean;
}

export type ChatAvatar =
  | 'signal'
  | 'vinyl'
  | 'wave'
  | 'star'
  | 'moon'
  | 'bolt'
  | 'lola'
  | 'mina'
  | 'sol'
  | 'luna'
  | 'echo'
  | 'aria'
  | 'hgr-admin'
  | 'hgr-police';
/** Nine tones, darkest to lightest. Numbered rather than named: any adjective
 *  for a skin tone is somebody's description of a person, and a number is not. */
export type ChatSkin =
  | 'tone-1' | 'tone-2' | 'tone-3'
  | 'tone-4' | 'tone-5' | 'tone-6'
  | 'tone-7' | 'tone-8' | 'tone-9';
/** Nine natural, silver, and two dyed in the station's own pink and aqua. */
export type ChatHair =
  | 'black' | 'blue-black' | 'dark-brown' | 'auburn'
  | 'ash-brown' | 'copper' | 'mid-brown' | 'caramel'
  | 'blonde' | 'silver' | 'pink' | 'aqua';
export type ChatDance = 'sway' | 'bounce' | 'shuffle' | 'spin' | 'robot' | 'jump';
export type ChatMovement = 'rooted' | 'glide' | 'orbit' | 'zigzag' | 'moonwalk' | 'float';
export type ChatShirt =
  | 'hgr-tee'
  | 'mesh'
  | 'track'
  | 'sunset'
  | 'workshirt'
  | 'no-shirt'
  | 'hgr-limited'
  /** Added in migration 0035. Ordinary stock, unlike 'hgr-limited' which is the
   *  claimable drop -- these three need no entitlement. */
  | 'rr-tee'
  | 'hgr-radio'
  | 'flytree';
export type ChatTrousers = 'black-jeans' | 'denim' | 'cargo' | 'track' | 'shorts';
export type ChatFloorRoom =
  | 'beach'
  | 'daylight'
  | 'sunset'
  | 'toilets'
  | 'lounge'
  | 'psychedelic'
  | 'ham-museum'
  | 'tattoo-studio'
  | 'spa'
  | 'club-terrace'
  | 'inside-club'
  | 'sunset-beach'
  | 'club-sundeck'
  /** Added in migration 0037. The beach club's toilet block, with a bar in it.
   *  The only room with a single way in -- see ROOM_EXIT in LiveChatPanel. */
  | 'club-toilets'
  /** Added in migration 0038. A small playable side room off the beach club. */
  | 'arcade-chase';
export type ChatDrink = 'none' | 'water' | 'cola' | 'lemonade' | 'ginger-beer' | 'beer';
export type ChatTattoo = 'none' | 'hgr' | 'star' | 'wave' | 'heart';
export type ChatCostume =
  | 'none'
  | 'cowboy'
  | 'alien'
  | 'scuba'
  | 'wizard'
  | 'bee'
  | 'cat'
  /** Added in migration 0036. 'robe' and 'mummy' point at the Night Spa and The
   *  Toilets, which had no costume of their own. */
  | 'robe'
  | 'mummy'
  | 'trex'
  | 'disco';
export type ChatAction = 'idle' | 'hands-up' | 'pushups' | 'clap' | 'wave' | 'point';

export interface ChatBadge {
  slug: string;
  title: string;
  description: string;
}

export interface ChatBadgeProgress {
  slug: string;
  title: string;
  current: number;
  target: number;
}

export interface ChatProfile {
  avatar: ChatAvatar;
  skin: ChatSkin;
  hair: ChatHair;
  dance: ChatDance;
  movement: ChatMovement;
  shirt: ChatShirt;
  trousers: ChatTrousers;
  floor_room: ChatFloorRoom;
  has_water: boolean;
  drink: ChatDrink;
  is_relaxing: boolean;
  tattoo: ChatTattoo;
  costume: ChatCostume;
  action: ChatAction;
  rank: string;
  member_since: string;
  message_count: number;
  badges: ChatBadge[];
  next_badge: ChatBadgeProgress | null;
}

export interface ChatIdentity extends ChatProfile {
  display_name: string;
  tag: string;
}

export interface ChatSender extends ChatProfile {
  name: string;
  /** A short, per-room discriminator for listeners who picked the same name. */
  tag: string;
}

export interface ChatPresence extends ChatProfile {
  name: string;
  tag: string;
  x: number;
  y: number;
}

export interface ChatVenueRoom {
  id: ChatFloorRoom;
  title: string;
  capacity: number;
  occupancy: number;
}

export interface ChatSpaPerson {
  name: string;
  tag: string;
  avatar: ChatAvatar;
}

export interface ChatSpaBouncer extends ChatSpaPerson {
  is_me: boolean;
  claimed_at: string;
}

export type ChatSpaEntryStatus = 'pending' | 'approved' | 'denied' | 'cancelled';

export interface ChatSpaEntryRequest {
  id: string;
  visitor: ChatSpaPerson;
  visitor_message: string;
  status: ChatSpaEntryStatus;
  response_message: string | null;
  created_at: string;
  decided_at: string | null;
}

export interface ChatSpaDoorState {
  bouncer: ChatSpaBouncer | null;
  my_request: ChatSpaEntryRequest | null;
  requests: ChatSpaEntryRequest[];
}

export interface ChatWardrobeItem {
  slug: string;
  kind: 'shirt';
  token: ChatShirt;
  name: string;
  description: string;
}

export interface ChatInventoryItem extends ChatWardrobeItem {
  acquired_at: string;
}

export interface RadioListeningStats {
  total_seconds: number;
  live_seconds: number;
  archive_seconds: number;
  message_count: number;
  items_collected: number;
  rank: string;
}

export interface RadioAchievement {
  slug: string;
  title: string;
  description: string;
  earned: boolean;
  progress: number;
  target: number;
}

export interface ChatAccount {
  username: string;
  is_admin: boolean;
  created_at: string;
  stats: RadioListeningStats;
  achievements: RadioAchievement[];
  inventory: ChatInventoryItem[];
}

export interface ChatDrop {
  id: string;
  item: ChatWardrobeItem;
  quantity: number;
  claimed_count: number;
  remaining: number;
  starts_at: string;
  ends_at: string;
  enabled: boolean;
  claimed_by_me: boolean;
}

export interface ChatMessage {
  id: number;
  sender: ChatSender;
  body: string;
  created_at: string;
}

/** Full snapshots are polled; message ids make replacement and unread counts safe. */
export interface ChatSnapshot {
  server_time: string;
  room: ChatRoom | null;
  me: ChatIdentity | null;
  messages: ChatMessage[];
  people: ChatPresence[];
  venue_rooms: ChatVenueRoom[];
  account: ChatAccount | null;
  drops: ChatDrop[];
  spa_door: ChatSpaDoorState;
  poll_after_ms: number;
}

export interface ChatAccountCredentials {
  username: string;
  password: string;
}

export interface RadioListeningHeartbeat {
  source: 'live' | 'archive';
  seconds: number;
}

export interface ChatJoinInput {
  display_name: string;
  avatar?: ChatAvatar;
  skin?: ChatSkin;
  hair?: ChatHair;
  dance?: ChatDance;
  movement?: ChatMovement;
  shirt?: ChatShirt;
  trousers?: ChatTrousers;
  costume?: ChatCostume;
}

export interface ChatMessageInput {
  room_id: string;
  /** Generated once per send so a retry cannot make the same message twice. */
  client_nonce: string;
  body: string;
}

export interface ChatPositionInput {
  x: number;
  y: number;
  floor_room?: ChatFloorRoom;
}

export interface ChatWaterInput {
  has_water: boolean;
}

export interface ChatDrinkInput {
  drink: ChatDrink;
}

export interface ChatRelaxInput {
  is_relaxing: boolean;
}

export interface ChatTattooInput {
  tattoo: ChatTattoo;
}

export interface ChatActionInput {
  action: ChatAction;
}

export interface ChatSpaBouncerInput {
  active: boolean;
}

export interface ChatSpaEntryRequestInput {
  message: string;
}

export interface ChatSpaEntryDecisionInput {
  approved: boolean;
  message: string;
}

export interface ChatDropCreateInput {
  item_slug: string;
  quantity: number;
  duration_minutes: number;
}

export interface ChatRoomAdminOut {
  id: string;
  enabled: boolean;
}

export interface ChatHiddenOut {
  id: number;
  hidden: boolean;
}

/* -------------------------------------------------------- GET /api/schedule */

export interface SlotCard {
  catalogue_no: string;
  title: string;
  starts_at: string;
  ends_at: string;
  kind: SlotKind;
  status: SlotStatus;
  /** Drives the (R) suffix. */
  is_repeat: boolean;
  hosts: HostRef[];
  genres: GenreRef[];
  artwork_color: ColorToken;
  /** Present once the booking has an episode to link to. */
  episode_catalogue_no: string | null;
}

export interface ScheduleDay {
  /** Station-local calendar date, `YYYY-MM-DD`. Not a timestamp. */
  date: string;
  /**
   * Sum of the booked windows. Gaps are deliberately NOT returned as slots —
   * the client draws the dark hours itself.
   */
  live_seconds: number;
  slots: SlotCard[];
}

export interface ScheduleResponse {
  /** The station's own zone, so a day boundary is labelled without guessing. */
  timezone: string;
  days: ScheduleDay[];
}

/* -------------------------------------------------------- GET /api/episodes */

/** Untimed by design — see docs/DATA_MODEL.md. */
export interface Track {
  position: number;
  artist: string;
  title: string;
  label: string | null;
}

export interface EpisodeCard {
  catalogue_no: string;
  title: string;
  show: ShowRef | null;
  hosts: HostRef[];
  genres: GenreRef[];
  broadcast_at: string;
  duration_seconds: number;
  artwork_color: ColorToken;
  /** Locally served episode cover; null uses the typographic station sleeve. */
  artwork_url: string | null;
  is_staff_pick: boolean;
  staff_pick_note: string | null;
  has_tracklist: boolean;
  location_short: string;
  /**
   * Which licence lets this episode's audio exist, and therefore which of four
   * pages the client draws. On the CARD as well as the detail, because the
   * archive grid is where the difference is visible at a glance.
   *
   * Not optional here even though the server defaults it: a card built without
   * it on this side would describe a permanent set as an expiring one or the
   * reverse, and the whole point of the field is that those are different.
   */
  clearance: Clearance;
  /**
   * When the Sena replay window closes for this episode, and null whenever no
   * clock is running — Tier A is permanent, Tier B has nothing of ours to take
   * down.
   *
   * NOT the same field as `audio_expires_at` on the detail. That one is the raw
   * column and stays populated after the window has closed, so a page can say
   * *when* it closed; this one answers "is a clock running, and until when", and
   * goes quiet the moment the answer is no.
   */
  replay_expires_at: string | null;
}

/**
 * `audio_url` is null on a Tier B episode and on a Tier C one whose 14-day
 * replay window has closed (D2). Both URLs null is a valid, renderable state
 * and must not be treated as an error: the tracklist, the notes and the credits
 * are the record even when there is nothing to press. `audio_expires_at` is
 * still returned after expiry so the page can say when the window closed rather
 * than pretend the file never existed.
 *
 * `mixcloud_url` IS A LINK, NOT AN EMBED. Mixcloud's Terms of Use §4 prohibit
 * widget use for "any form of commercial use or non personal / individual use
 * (unless pursuant to prior written consent…)" — an OR, so being non-profit
 * answers only the first prong, and a station site is not a personal website.
 * components/MixcloudCard.tsx is what this field renders as.
 */
export interface EpisodeDetail extends EpisodeCard {
  standfirst: string | null;
  body: string | null;
  location_long: string;
  /** Public canonical track URL, played through SoundCloud's official widget. */
  soundcloud_url: string | null;
  mixcloud_url: string | null;
  audio_url: string | null;
  audio_expires_at: string | null;
  waveform_url: string | null;
  tracks: Track[];
  related: EpisodeCard[];
}

/** The shared repeating SoundCloud programme between scheduled live shows. */
export interface SoundCloudSchedule {
  server_time: string;
  starts_at: string;
  ends_at: string;
  elapsed_seconds: number;
  remaining_seconds: number;
  current: EpisodeDetail;
  next: EpisodeCard[];
}

export interface EpisodeList {
  total: number;
  limit: number;
  offset: number;
  results: EpisodeCard[];
}

/* ------------------------------------------------------- GET /api/premieres
 *
 * WHY A PREMIERE IS ITS OWN OBJECT AND NOT A KIND OF EPISODE.
 *
 * D14: the station holds no collective licence, so every recording that goes
 * out has to be individually clear. For a RELEASED record that is not merely
 * hard, it is the wrong instrument — for a published phonogram the exclusive
 * broadcasting right does not exist at all; a statutory remuneration right
 * replaces it, and that claim is UNWAIVABLE in Germany (§78(3) UrhG) and Spain
 * (art. 108.3 TRLPI). So a direct grant from the rights holder does not answer
 * it. It is not defective consent, it is consent to the wrong question.
 *
 * And "released" is wider than it sounds. Art. 7(2) WNR counts a phonogram
 * "made available to the public", and §78(1) UrhG says "published OR lawfully
 * made available online" — a Bandcamp-only, self-released underground record is
 * inside that system, not outside it.
 *
 * An UNRELEASED recording is the one case where the reasoning inverts. Every
 * exception that strips the performer's exclusive right turns on the recording
 * having been published or made available; for a track that has never been
 * released anywhere the performer's EXCLUSIVE right is live (art. 2(1)(d) WNR),
 * direct consent is fully effective, and no remuneration claim arises. That is
 * what a premiere is, and it is why this section of the station can be cleared
 * with an email while the archive cannot.
 *
 * Which is why `release_date` and `is_unreleased` are load-bearing fields and
 * not decoration: A PREMIERE STOPS BEING STRUCTURALLY CLEAN ON ITS RELEASE
 * DATE. It does not become unlawful — the consent we hold still stands — but
 * from that day the recording is published, the remuneration layer attaches,
 * and the reasoning behind the page is a different one. pages/Premiere.tsx says
 * a different thing on each side of that date, and this is why.
 */

/**
 * The booking a premiere first went out in. Deliberately the three fields every
 * slot shape on this wire already carries (`SlotCard`, `UpNext`), so the page
 * can name the show without the premieres endpoint having to return a whole
 * schedule row.
 */
export interface SlotRef {
  catalogue_no: string;
  title: string;
  starts_at: string;
}

/**
 * One track, debuted before it comes out.
 *
 * Note what is NOT on this card and is on an `EpisodeCard`: no runtime, no host
 * roster, no tracklist flag. A premiere is a single record, so the facts that
 * matter are the artist, the title, the label and the release date — see
 * components/PremiereCard.tsx.
 */
export interface PremiereCard {
  slug: string;
  artist_name: string;
  track_title: string;
  label_name: string;
  /** The label's own number, e.g. `FLY-004`. The sleeve face comes off this. */
  release_catalogue: string;
  /**
   * A station-local CALENDAR DATE (`YYYY-MM-DD`), like `ScheduleDay.date` and
   * unlike every timestamp on this wire: a record comes out on a day, not at a
   * moment. format.ts renders a date-only value in UTC so the calendar date
   * survives being read in Chicago.
   */
  release_date: string;
  duration_seconds: number;
  artwork_color: ColorToken;
  /** When it went out, or goes out. ISO-8601 UTC. */
  premieres_at: string;
  genres: GenreRef[];
  /** The resident who played it. Null for a station premiere with no show. */
  host: HostRef | null;
  /**
   * THE SERVER'S ANSWER, NEVER A DATE COMPARISON HERE.
   *
   * "Has this recording been published or made available anywhere" is not the
   * same question as "is `release_date` in the future" — art. 7(2) WNR and
   * §78(1) UrhG both count a phonogram merely made available online, so a
   * Bandcamp page put up early flips this to false with no release date moving.
   * Only the station knows that, so only the station answers it, and the client
   * renders the answer rather than re-deriving one that would sometimes differ.
   */
  is_unreleased: boolean;
}

export interface PremiereDetail extends PremiereCard {
  blurb: string | null;
  /**
   * Where to buy or pre-order it, at the artist's or label's own shop. Null
   * until they have one up — the page must then say so rather than draw a
   * button that goes nowhere.
   */
  buy_url: string | null;
  waveform_url: string | null;
  /**
   * The self-hosted file. Null before the premiere has aired.
   *
   * The on-demand carve-out in art. 7(1) WNR leaves TWO exclusive rights
   * standing — the producer's (art. 6(1)(c)) and the PERFORMER'S
   * (art. 2(1)(d)) — so a producer-only grant does not reach this field. The
   * clearance behind an audio file on this page has to name the performers.
   */
  audio_url: string | null;
  slot: SlotRef | null;
}

export interface PremiereList {
  total: number;
  limit: number;
  offset: number;
  results: PremiereCard[];
}

/**
 * The identity a premiere is carried under wherever the rest of the app expects
 * a catalogue number — the sleeve face, the player bar's mono line, the saved
 * playback position.
 *
 * The RELEASE catalogue, because that is the number actually printed on the
 * record and the one a listener can act on. `PRE` covers a premiere the label
 * has not numbered yet: short on purpose, since this string is also set as the
 * big display face on a sleeve (components/Artwork.tsx) and a long word there
 * is a smudge.
 */
export function premiereCatalogueNo(premiere: PremiereCard): string {
  // `|| ''` for the same reason `api.getHosts` writes `?? []`: the field is
  // non-null on the wire, and a card grid should degrade to a sleeve with no
  // number rather than throw a TypeError if a server ever omits it.
  return (premiere.release_catalogue || '').trim().toUpperCase() || 'PRE';
}

/**
 * A premiere in the shape the persistent player speaks.
 *
 * THERE IS ONE `<audio>` ELEMENT ON THIS STATION and it lives above the router
 * (App.tsx), so the only way to start a premiere without building a second
 * player that dies on navigation is `usePlayer().playEpisode`, which takes an
 * `EpisodeDetail`. This is that adaptation, in one place, so that neither page
 * hand-rolls it and neither page is tempted to mount an `<audio>` of its own.
 *
 * TWO FIELDS ARE NOT COSMETIC:
 *
 * `clearance` is `direct_licence` — the member that means exactly "a named
 * grantor gave written permission for these specific recordings", which is what
 * a premiere is (art. 2(1)(d) WNR for the performers, art. 6(1)(c) for the
 * producer). It must NOT be `sena_replay`: the player strips the scrubber on
 * Tier C audio because Sena art. 4(e) allows only a fixed forward jump, and the
 * station holds no Sena licence for that condition to come from (D14). No
 * replay clock is running on a premiere, so `replay_expires_at` and
 * `audio_expires_at` are null and the transport is the ordinary one.
 *
 * `title` is `artist — track`, because in the player bar that string is the
 * whole readout and "Dust in the Machine" on its own does not say whose.
 */
export function premiereAsPlayable(premiere: PremiereDetail): EpisodeDetail {
  return {
    catalogue_no: premiereCatalogueNo(premiere),
    title: `${premiere.artist_name} — ${premiere.track_title}`,
    show: null,
    hosts: premiere.host ? [premiere.host] : [],
    genres: premiere.genres,
    broadcast_at: premiere.premieres_at,
    duration_seconds: premiere.duration_seconds,
    artwork_color: premiere.artwork_color,
    artwork_url: null,
    is_staff_pick: false,
    staff_pick_note: null,
    has_tracklist: false,
    // A premiere is not tied to a studio. The player joins these with
    // `.filter(Boolean)`, so an empty string is one fewer separator rather than
    // an invented location.
    location_short: '',
    location_long: '',
    clearance: 'direct_licence',
    replay_expires_at: null,
    standfirst: premiere.blurb,
    body: null,
    soundcloud_url: null,
    mixcloud_url: null,
    audio_url: premiere.audio_url,
    audio_expires_at: null,
    waveform_url: premiere.waveform_url,
    tracks: [],
    related: [],
  };
}

/* ----------------------------------------------------------- GET /api/hosts */

export type ResidentRank =
  | 'Recruit'
  | 'Soldier'
  | 'Sergeant'
  | 'Lieutenant'
  | 'Captain'
  | 'Colonel';

export interface HostCard {
  slug: string;
  name: string;
  city: string | null;
  is_resident: boolean;
  /** `archived` means "no longer on the schedule", never "deleted". */
  status: HostStatus;
  artwork_color: ColorToken;
  /** Public square portrait uploaded by staff; null keeps the monogram tile. */
  artwork_url: string | null;
  /**
   * Two characters, computed on the server so every sleeve agrees. "de Wit" is
   * WI, not DW — do not re-derive this in the client.
   */
  monogram: string;
  episode_count: number;
  resident_rank: ResidentRank | null;
  latest: EpisodeCard | null;
}

export interface HostStats {
  episode_count: number;
  seconds_on_air: number;
}

export interface HostDetail extends HostCard {
  bio: string | null;
  pronouns: string | null;
  /** { instagram, soundcloud, bandcamp, mixcloud, website } — all optional. */
  links: Record<string, string>;
  /** `YYYY-MM-DD`, or null for a guest. */
  resident_since: string | null;
  genres: GenreRef[];
  shows: ShowRef[];
  /** All of them, newest first. */
  episodes: EpisodeCard[];
  upcoming: SlotCard[];
  stats: HostStats;
}

/** The roster envelope as it comes off the wire; `api.getHosts` unwraps it. */
export interface HostList {
  results: HostCard[];
}

/* ---------------------------------------------------------- GET /api/genres */

export interface GenreOut extends GenreRef {
  /**
   * Published episodes only. A chip that returns nothing when you press it is
   * worse than a chip that is not there, so the count is what decides whether
   * the archive draws it.
   */
  episode_count: number;
}

/* --------------------------------------------------- POST /api/submissions */

/**
 * The open call (D4): a LINK, never a file. Every field here is validated on
 * the server and comes back as a 422 keyed to the field it belongs to, so the
 * form renders each message under its own input.
 */
export interface SubmissionInput {
  kind: SubmissionKind;
  format: SubmissionFormat;
  artist_name: string;
  real_name: string | null;
  email: string;
  city: string | null;
  link_url: string;
  pitch: string;
  /** Three maximum, and every slug must already exist. */
  genre_slugs: string[];
  /** Must be true. We store the timestamp and the IP. */
  rights_accepted: boolean;
}

/** 201. Deliberately thin — the open call never echoes internal state back. */
export interface SubmissionOut {
  id: string;
  message: string;
}

/* --------------------------------------------------------------- admin */

export interface StaffOut {
  id: string;
  email: string;
  name: string;
  role: StaffRole;
  last_login_at: string | null;
}

/**
 * The automatic checks. EVERY FIELD IS TRI-STATE: null means "not checked yet",
 * which is a different thing from false and has to look different on screen. A
 * grey dot is not a red one.
 */
export interface SubmissionChecks {
  link_resolves: boolean | null;
  duration_in_range: boolean | null;
  not_duplicate: boolean | null;
  pitch_long_enough: boolean | null;
  not_direct_download: boolean | null;
  loudness_ok: boolean | null;
  /** Free text per check, e.g. { duration_in_range: "94 min · in range" }. */
  detail: Record<string, string>;
}

export interface IngestStatus {
  job_id: string | null;
  /** queued | running | complete | failed | unknown */
  status: string;
  step: string | null;
  detail: string | null;
  updated_at: string | null;
}

export interface SubmissionEventOut {
  from_state: SubmissionState | null;
  to_state: SubmissionState;
  actor: string;
  reason: string | null;
  created_at: string;
}

export type AssetKind = 'original' | 'delivery' | 'artwork' | 'peaks';

export interface AssetOut {
  id: string;
  kind: AssetKind;
  bytes: number | null;
  content_type: string | null;
  created_at: string;
  /** D6. The measured and applied numbers, so we can show what happened. */
  loudness: Record<string, unknown> | null;
}

/** What we can honestly say about a minted link: never the token, only a hint. */
export interface SubmissionLinkOut {
  hash_hint: string;
  expires_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
  created_at: string;
}

export interface AdminSubmissionRow {
  id: string;
  state: SubmissionState;
  kind: SubmissionKind;
  format: SubmissionFormat;
  artist_name: string;
  real_name: string | null;
  email: string;
  city: string | null;
  link_url: string;
  pitch: string;
  genre_slugs: string[];
  created_at: string;
  assigned_to: string | null;
  assignee: StaffRef | null;
  slot: SlotCard | null;
  checks: SubmissionChecks;
  ingest: IngestStatus | null;
  assets: AssetOut[];
  events: SubmissionEventOut[];
  link: SubmissionLinkOut | null;
  /**
   * Straight from the server's state machine. The queue renders exactly these
   * buttons and no others, so it can never offer a move the API will reject.
   */
  allowed_transitions: SubmissionState[];
}

export interface AdminSubmissionList {
  total: number;
  limit: number;
  offset: number;
  results: AdminSubmissionRow[];
}

export interface TransitionRequest {
  to_state: SubmissionState;
  /**
   * Optional to the schema, required by editorial habit: a rejection with no
   * reason is one nobody can explain three months later.
   */
  reason?: string | null;
  /** Booking a slot on acceptance. */
  slot_id?: string | null;
}

/**
 * A booked slot inside 13 days whose host has no contactable email — the
 * failure that would otherwise make the whole reminder chain a silent no-op,
 * three days before someone's show.
 */
export interface PreflightRow {
  catalogue_no: string;
  title: string;
  starts_at: string;
  days_until: number;
  hosts: HostRef[];
  reason: string;
}

/** The envelope as it comes off the wire; `api.adminPreflight` unwraps it. */
export interface PreflightOut {
  results: PreflightRow[];
}

export interface AdminStats {
  /** submission_state -> count, for the queue tabs. */
  queue: Record<string, number>;
  /** Null until enough submissions have had a first reply to have a median. */
  median_days_to_first_reply: number | null;
  open_slots_next_14_days: number;
  episodes_published: number;
}

export interface LoginRequest {
  email: string;
  password: string;
}

/** The plain acknowledgement the logout endpoint returns. */
export interface Ack {
  ok: boolean;
}

/* --------------------------------------------------------------- site pages */

export interface AboutPageContent {
  page_title: string;
  page_meta: string;
  story_heading: string;
  story_body: string;
  station_heading: string;
  station_body: string;
  support_heading: string;
  support_body: string;
  location_heading: string;
  location_body: string;
  accessibility_heading: string;
  accessibility_body: string;
  conduct_heading: string;
  conduct_intro: string;
  conduct_items: string[];
  conduct_outro: string;
  contact_heading: string;
  contact_meta: string;
  contact_email: string;
  contact_description: string;
  credits_heading: string;
  credits_meta: string;
  credits_body: string;
}

/** Dirty-fields-only patch; every public field remains required once merged. */
export type AboutPageUpdate = Partial<AboutPageContent>;

/* ------------------------------------------------------------------ cue desk */

/**
 * One recording the operator can monitor, cleared or not.
 *
 * The station holds no collective licence (D14), so most of what it has on disk
 * is `uncleared` and holds no `audio_url`. That is publication. This is
 * monitoring: an operator has to be able to HEAR a set in order to decide
 * whether it can be cleared, so `clearance` is shown but never filters.
 */
export interface CueItem {
  kind: 'episode' | 'premiere';
  ident: string;
  title: string;
  billing: string;
  broadcast_at: string | null;
  duration_seconds: number | null;
  clearance: string;
  published: boolean;
  /** False when the row points at a file that is no longer on disk. */
  available: boolean;
  bytes: number | null;
  cue_url: string;
}

export interface CueList {
  items: CueItem[];
  missing: number;
}

/* ------------------------------------------------------------------- studio
 *
 * The operator's desk. `hgr/schemas.py`, everything under "# ---- studio".
 *
 * TWO RULES FROM THAT FILE ARE LOAD-BEARING HERE AND NOT JUST ON THE SERVER.
 *
 * 1. EVERY UPDATE SHAPE IS DIRTY-FIELDS-ONLY. The handlers read
 *    `model_dump(exclude_unset=True)`, so an ABSENT key means "leave alone" and
 *    an EXPLICIT null means "clear it". That is why every field on the `*Update`
 *    interfaces below is optional, and why the desk builds a patch object by
 *    adding keys rather than by copying a form: sending a whole form back would
 *    make a second open tab able to revert a field it never touched. There is no
 *    version column, no ETag and no 409 on this surface because of this rule, so
 *    breaking it here has nothing underneath it to catch the mistake.
 *
 * 2. `clearance` AND `status` ARE PLAIN SETTABLE FIELDS. No required evidence,
 *    no confirmation, nothing that refuses a tier. See clearance.ts for the
 *    words the desk puts on them and for why it does not editorialise.
 *
 * `clearance` and `status` are typed `string` rather than as unions, matching
 * the schemas — those fields are `str` on `StudioEpisodeRow`, and a union here
 * would make a sixth enum member a compile error in a page that only wants to
 * print it. clearance.ts narrows when it needs to.
 */

/** One rendered image, as the desk shows it. `url` is null with no public base. */
export interface ArtworkOut {
  key: string;
  url: string | null;
  size: number;
  bytes: number;
}

/**
 * The measured output of a prepared delivery, straight off `assets.loudness`.
 *
 * `dict[str, Any]` on the wire, so `Record<string, unknown>` here and not a
 * struct: the shape is `audio.NormalizeResult.to_json()` today and the desk must
 * not turn a renamed key into a crash. `readLoudness` in the audio panel pulls
 * the numbers out with a typeof check each.
 */
export type LoudnessJson = Record<string, unknown>;

/* ------------------------------------------------------------ studio episodes */

/** One line in the episodes list. Enough to decide what to open. */
export interface StudioEpisodeRow {
  id: string;
  catalogue_no: string;
  title: string;
  broadcast_at: string;
  duration_seconds: number;
  status: string;
  clearance: string;
  /** True only when a listener can play this episode through its public page now. */
  published: boolean;
  /** A local delivery or SoundCloud track is stored, even if it is not publicly exposed. */
  has_playback_provider: boolean;
  has_master: boolean;
  has_delivery: boolean;
  waveform_url: string | null;
  artwork_url: string | null;
  artwork_color: string;
  hosts: HostRef[];
}

/** Every editable column of one episode, plus what is on disk for it. */
export interface StudioEpisodeDetail extends StudioEpisodeRow {
  show_id: string | null;
  standfirst: string | null;
  body: string | null;
  location_short: string | null;
  location_long: string | null;
  soundcloud_url: string | null;
  mixcloud_url: string | null;
  audio_url: string | null;
  audio_expires_at: string | null;
  artwork_key: string | null;
  is_staff_pick: boolean;
  staff_pick_note: string | null;
  clearance_id: string | null;
  genre_slugs: string[];
  tracks: Track[];
  master_path: string | null;
  master_bytes: number | null;
  loudness: LoudnessJson | null;
  /** One neutral sentence about publication state. Never a warning. */
  publish_note: string;
}

/**
 * A new episode. Only a title is required — `catalogue_no` is allocated by
 * hgr.catalogue when omitted, across both episodes and slots.
 */
export interface StudioEpisodeCreate {
  title: string;
  catalogue_no?: string | null;
  broadcast_at?: string | null;
  duration_seconds?: number | null;
  host_slugs?: string[];
  show_id?: string | null;
  standfirst?: string | null;
  body?: string | null;
  location_short?: string | null;
  location_long?: string | null;
  artwork_color?: string | null;
  genre_slugs?: string[];
}

/** A dirty-fields PATCH. Absent means leave alone; null means clear. */
export interface StudioEpisodeUpdate {
  title?: string;
  broadcast_at?: string | null;
  duration_seconds?: number | null;
  host_slugs?: string[] | null;
  show_id?: string | null;
  standfirst?: string | null;
  body?: string | null;
  location_short?: string | null;
  location_long?: string | null;
  soundcloud_url?: string | null;
  mixcloud_url?: string | null;
  artwork_color?: string | null;
  genre_slugs?: string[] | null;
  tracks?: TrackIn[] | null;
  is_staff_pick?: boolean | null;
  staff_pick_note?: string | null;
  /** Exposure. Settable outright — see rule 2 above. */
  status?: string | null;
  clearance?: string | null;
  clearance_id?: string | null;
}

/** A tracklist row on the way in. `Track` is the same shape coming back. */
export interface TrackIn {
  position: number;
  artist: string;
  title: string;
  label: string | null;
}

/* --------------------------------------------------------------- studio hosts */

export interface StudioHostRow {
  id: string;
  slug: string;
  name: string;
  is_resident: boolean;
  status: string;
  city: string | null;
  artwork_color: string;
  artwork_url: string | null;
  has_bio: boolean;
  has_email: boolean;
  episode_count: number;
  resident_rank: ResidentRank | null;
}

export interface StudioHostDetail extends StudioHostRow {
  pronouns: string | null;
  bio: string | null;
  email: string | null;
  /** Free-form label -> URL. The column is jsonb; the desk edits it as rows. */
  links: Record<string, string>;
  resident_since: string | null;
  artwork_key: string | null;
}

export interface StudioHostCreate {
  name: string;
  slug?: string | null;
  is_resident?: boolean;
  pronouns?: string | null;
  city?: string | null;
  bio?: string | null;
  email?: string | null;
  links?: Record<string, string> | null;
  artwork_color?: string | null;
}

export interface StudioHostUpdate {
  name?: string;
  is_resident?: boolean | null;
  status?: string | null;
  pronouns?: string | null;
  city?: string | null;
  bio?: string | null;
  email?: string | null;
  links?: Record<string, string> | null;
  artwork_color?: string | null;
  resident_since?: string | null;
}

/* --------------------------------------------------------------- studio shows */

export interface StudioShowRow {
  id: string;
  slug: string;
  title: string;
  kind: string;
  cadence: string | null;
  active: boolean;
  artwork_color: string;
  artwork_url: string | null;
  episode_count: number;
}

export interface StudioShowDetail extends StudioShowRow {
  tagline: string | null;
  description: string | null;
  artwork_key: string | null;
  host_slugs: string[];
}

export interface StudioShowCreate {
  title: string;
  slug?: string | null;
  kind?: string | null;
  cadence?: string | null;
  tagline?: string | null;
  description?: string | null;
  artwork_color?: string | null;
  host_slugs?: string[];
}

export interface StudioShowUpdate {
  title?: string;
  kind?: string | null;
  cadence?: string | null;
  tagline?: string | null;
  description?: string | null;
  artwork_color?: string | null;
  active?: boolean | null;
  host_slugs?: string[] | null;
}

/* ---------------------------------------------------------- studio premieres
 *
 * The premieres tab talks to the EXISTING admin API — `/api/admin/premieres` —
 * not to a studio route, because a premiere was already editable from there
 * before this page existed. `admin.list_admin_premieres` returns
 * `list[PremiereDetail]` with the embargo lifted, which is why `audio_url` is
 * here at all: an operator has to be able to check the right file is attached
 * BEFORE the debut, since the only other moment to notice is afterwards.
 *
 * `id` IS OPTIONAL AND THAT IS NOT A STYLE CHOICE. `PATCH /api/admin/premieres/
 * {premiere_id}` takes a UUID, and `schemas.PremiereCard` / `PremiereDetail` do
 * not carry `id` — so as the serialiser stands today the row this list hands back
 * cannot address its own PATCH. The desk therefore treats an id as a capability:
 * present, the form saves; absent, the form says plainly that it cannot and why.
 * The moment the serialiser gains the field, editing starts working with no
 * change here.
 *
 * Note `released_on`, not `release_date`: the response spells it the first way
 * (see `premieres.premiere_detail`) and the request bodies spell it the second.
 * Two names for one date is a trap worth naming rather than smoothing over.
 */
export interface StudioPremiere {
  id?: string;
  slug: string;
  artist_name: string;
  track_title: string;
  label_name: string | null;
  release_catalogue: string | null;
  premieres_at: string;
  duration_seconds: number;
  artwork_color: string;
  status: string;
  clearance: string;
  blurb: string | null;
  buy_url: string | null;
  audio_url: string | null;
  waveform_url: string | null;
  genres: GenreRef[];
  host: HostRef | null;
  slot: SlotCard | null;
  released_on: string | null;
  is_unreleased: boolean;
}

export interface StudioPremiereCreate {
  slug: string;
  artist_name: string;
  track_title: string;
  duration_seconds: number;
  premieres_at: string;
  host_slug?: string | null;
  label_name?: string | null;
  release_catalogue?: string | null;
  release_date?: string | null;
  buy_url?: string | null;
  blurb?: string | null;
  artwork_color?: string;
  genre_slugs?: string[];
  clearance?: string;
  status?: string;
}

export interface StudioPremiereUpdate {
  artist_name?: string;
  host_slug?: string | null;
  track_title?: string;
  label_name?: string | null;
  release_catalogue?: string | null;
  release_date?: string | null;
  buy_url?: string | null;
  blurb?: string | null;
  duration_seconds?: number | null;
  artwork_color?: string | null;
  premieres_at?: string | null;
  genre_slugs?: string[] | null;
  clearance?: string | null;
  clearance_id?: string | null;
  status?: string | null;
}

/* ---------------------------------------------------------------- clearances
 *
 * The evidence side of `episodes.clearance`: who asserted a permission, over
 * what, and when. ADMIN ONLY — `grantor_email` is usually a private
 * individual's address, and it must never reach a public shape.
 */
export interface ClearanceOut {
  id: string;
  grantor_name: string;
  grantor_email: string;
  scope: string;
  granted_at: string;
  tracks_covered: string | null;
  notes: string | null;
  has_document: boolean;
  document_r2_key: string | null;
  created_at: string;
}

/**
 * A grant somebody actually gave. Only `grantor_name` is required, and creating
 * one at all is optional — a `direct_licence` with no grant row saves fine.
 */
export interface ClearanceCreate {
  grantor_name: string;
  grantor_email?: string | null;
  scope?: string;
  tracks_covered?: string | null;
  notes?: string | null;
}

/* ------------------------------------------------------------ files + import */

/** One candidate on disk, as the file browser shows it. */
export interface StudioFile {
  path: string;
  name: string;
  bytes: number;
  modified_at: string;
  /** What the filename parser made of it, shown BEFORE it becomes a row. */
  title: string | null;
  dj_slugs: string[];
  broadcast_at: string | null;
  /** Whether the date is a fact or a guess, and which fact. */
  date_source: string | null;
  inferred: string[];
  /** True when some episode already points an ORIGINAL asset at this path. */
  already_imported: boolean;
  imported_as: string | null;
}

export interface StudioFileList {
  root: string;
  files: StudioFile[];
  /** Roots the operator is allowed to browse, so the desk can offer them. */
  allowed_roots: string[];
}

/** Turn files on disk into episodes. Nothing is copied and nothing is published. */
export interface StudioImportRequest {
  paths: string[];
  /** Assign every created episode to these hosts, overriding the filename guess. */
  host_slugs?: string[] | null;
}

export interface StudioImportedRow {
  path: string;
  catalogue_no: string | null;
  title: string | null;
  /** 'created' | 'already-imported' | 'refused' */
  outcome: string;
  detail: string | null;
}

export interface StudioImportResult {
  rows: StudioImportedRow[];
  created: number;
}

/* -------------------------------------------------------------------- delivery
 *
 * There is no job queue behind this. The request runs the transcode and stays
 * open for as long as it takes, which on a four-hour set is minutes. The desk
 * says so in as many words next to the button, because the one thing worse than
 * a slow operation is a slow operation the operator thinks has failed.
 */
export interface DeliveryRequest {
  /** False prepares without touching the public directory — a level check. */
  publish: boolean;
  force: boolean;
}

export interface DeliveryOut {
  ident: string;
  /** 'transcoded' | 'cached' */
  outcome: string;
  published: boolean;
  audio_url: string | null;
  waveform_url: string | null;
  loudness: LoudnessJson | null;
  /** One neutral sentence about publication state. */
  publish_note: string;
  notes: string[];
}

/* --------------------------------------------------------------- going live
 *
 * `GET /api/admin/studio/live` — the go-live panel (pages/studio/GoLive.tsx).
 *
 * The shapes are declared in `hgr/routers/studio_live.py` rather than in
 * schemas.py, because they are one screen's read model. Two things about them
 * are load-bearing rather than incidental:
 *
 * 1. THERE IS NO KEY FIELD, AND THERE MUST NEVER BE ONE. `StudioLiveKey` carries
 *    a label and two timestamps. Not the key, not a prefix of it, not its hash —
 *    `/internal/dj/auth` can be retried without limit, so a "first six
 *    characters, for identification" convenience would be a head start on a
 *    brute force. `hg live <host>` is the only thing that ever prints a key, and
 *    it prints it once.
 *
 * 2. `can_connect_now` IS THE CONJUNCTION THE SERVER APPLIES, not a hint the
 *    panel recomputes. A key alone does not get anybody on air: auth also
 *    requires a slot of that host's to be open at that instant, and the panel
 *    must not be able to disagree with the endpoint that will actually decide.
 */

/** The five settings an operator reads out. Port 8005, never 8000. */
export interface StudioLiveEncoder {
  server_type: string;
  host: string;
  port: number;
  mount: string;
  format: string;
  /** What the station goes out at. */
  out_bitrate_kbps: number;
  /** What we ask the DJ to send, which is deliberately higher. */
  send_bitrate_kbps: number;
  samplerate: number;
  channels: number;
  tls: boolean;
  listen_url: string;
  /** One sentence about the host field — in production it is not the DJ host. */
  host_note: string;
}

/** A usable key, described without being disclosed. See note 1 above. */
export interface StudioLiveKey {
  label: string;
  created_at: string;
  last_used_at: string | null;
}

export interface StudioLiveSlot {
  catalogue_no: string;
  title: string;
  starts_at: string;
  ends_at: string;
  kind: string;
  status: string;
  /** True for the slot covering this instant, per `internal._open_slot_now`. */
  open_now: boolean;
}

export interface StudioLiveHost {
  slug: string;
  name: string;
  is_resident: boolean;
  has_key: boolean;
  keys: StudioLiveKey[];
  /** Their slot covering this instant. Null is the normal state. */
  open_slot: StudioLiveSlot | null;
  /** Their next live booking inside a fortnight, or null. */
  next_slot: StudioLiveSlot | null;
  can_connect_now: boolean;
  /** Why not, in the words dj_auth denies with. Null when they are ready. */
  blocker: string | null;
}

export interface StudioLiveLink {
  /** 'icecast' | 'liquidsoap' */
  name: string;
  ok: boolean;
  /** Where the API looked. Naming it is half the diagnosis. */
  target: string;
  detail: string;
  /** The command to paste, or null when there is nothing to do. */
  fix: string | null;
}

export interface StudioLiveState {
  at: string;
  encoder: StudioLiveEncoder;
  hosts: StudioLiveHost[];
  chain: StudioLiveLink[];
  /** The station-wide open slot, whoever it belongs to. */
  open_slot: StudioLiveSlot | null;
  /** Set when the open slot has no host attached, so no key opens it. */
  slot_note: string | null;
  /** 'set' | 'placeholder' | 'unset'. Never the key. */
  internal_key_state: string;
  internal_key_note: string | null;
  chain_ready: boolean;
}
